import * as React from 'react';

import classNames from 'classnames';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { useTracker } from '@coursera/event-pulse/react';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { planAutoRenewDate } from 'bundles/teams-v2/components/utils';

import _t from 'i18n!nls/teams-v2';

import 'css!./__styles__/TeamsLegalDisclaimerItem';

const ORGANIZATION_AGREEMENT_LINK = 'https://www.coursera.org/about/terms-for-enterprise';
const TERMS_OF_USE_LINK = 'https://www.coursera.org/about/terms';
const PRIVACY_POLICY_LINK = 'https://www.coursera.org/about/privacy';

type PropsToComponent = {
  isDiscounted?: boolean;
};

type buttonNameType =
  | 'wes_funnel_organization_agreement_link'
  | 'wes_funnel_terms_of_use_link'
  | 'wes_funnel_privacy_policy_link'
  | 'wes_funnel_processing_data_link';

const TeamsLegalDisclaimerItem = ({ isDiscounted }: PropsToComponent) => {
  const legal = isDiscounted
    ? _t(
        `By confirming this purchase, I agree to the {courseraOrganizationsAgreement}. 
    Your annual subscription begins today. If you decide to stop auto-renewal, 
    visit your plan Account page to cancel before {planAutoRenewDate} and your 
    subscription won't renew. If you would like to cancel your current plan, 
    you have 14 days to request a full refund from the date of purchase. 
    Check your confirmation email for details. If you received an offer for reduced pricing, 
    this pricing may not be available upon renewal or when adding new licenses. 
    Coursera may also share aggregate data back to affiliate partners to evaluate 
    the effectiveness of the partnership.`
      )
    : _t(
        `By confirming this purchase, I agree to the {courseraOrganizationsAgreement}. 
    Your annual subscription begins today. If you decide to stop auto-renewal, 
    visit your plan Account page to cancel before {planAutoRenewDate} and your 
    subscription won't renew. If you would like to cancel your current plan, 
    you have 14 days to request a full refund from the date of purchase. 
    Check your confirmation email for details.`
      );

  const track = useTracker();

  const handleEventingV3ForClick = (buttonName: buttonNameType) => {
    track('click_button', {
      button: {
        name: buttonName,
      },
    });
  };

  return (
    <div
      className={classNames('rc-TeamsLegalDisclaimerItem', 'm-t-2', 'rc-TeamsLegalDisclaimerItem--padding')}
      data-testid="teams-legal-disclaimer-item"
    >
      <p className="c-disclaimerText m-b-1">
        <FormattedMessage
          message={legal}
          courseraOrganizationsAgreement={
            <TrackedA
              trackingName="organizations_agreement_link"
              target="_blank"
              rel="noopener noreferrer"
              href={ORGANIZATION_AGREEMENT_LINK}
              data-testid="organizations-agreement-link"
              className="c-disclaimerLink"
              onClick={() => handleEventingV3ForClick('wes_funnel_organization_agreement_link')}
            >
              {_t('Coursera Organizations Agreement')}
            </TrackedA>
          }
          planAutoRenewDate={planAutoRenewDate}
        />
      </p>
      <p className="c-disclaimerText m-b-0">
        <FormattedMessage
          message={_t(`I understand I am also creating a new Coursera account. By signing up to create an account I accept
            Coursera's {termsOfUse} and {privacyPolicy}, including the {processingOfPersonalData}.`)}
          termsOfUse={
            <TrackedA
              trackingName="terms_of_use_link"
              target="_blank"
              rel="noopener noreferrer"
              href={TERMS_OF_USE_LINK}
              className="c-disclaimerLink"
              data-testid="terms-of-use-link"
              onClick={() => handleEventingV3ForClick('wes_funnel_terms_of_use_link')}
            >
              {_t('Terms of Use')}
            </TrackedA>
          }
          privacyPolicy={
            <TrackedA
              trackingName="privacy_policy_link"
              target="_blank"
              rel="noopener noreferrer"
              href={PRIVACY_POLICY_LINK}
              className="c-disclaimerLink"
              data-testid="privacy-policy-link"
              onClick={() => handleEventingV3ForClick('wes_funnel_privacy_policy_link')}
            >
              {_t('Privacy Policy')}
            </TrackedA>
          }
          processingOfPersonalData={
            <TrackedA
              trackingName="processing_data_link"
              target="_blank"
              rel="noopener noreferrer"
              href={PRIVACY_POLICY_LINK}
              className="c-disclaimerLink"
              data-testid="processing-data-link"
              onClick={() => handleEventingV3ForClick('wes_funnel_processing_data_link')}
            >
              {_t('processing of your personal data in the United States')}
            </TrackedA>
          }
        />
      </p>
    </div>
  );
};

export default TeamsLegalDisclaimerItem;
